import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "@phosphor-icons/react";

const KEY = "md_cookie_consent_v1";

export const useCookieConsent = () => {
  const [consent, setConsent] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || "null"); } catch { return null; }
  });
  // Keep every consumer (banner, AnalyticsInjector, …) in sync the moment consent changes.
  useEffect(() => {
    const sync = () => {
      try { setConsent(JSON.parse(localStorage.getItem(KEY) || "null")); } catch { setConsent(null); }
    };
    window.addEventListener("md:consent-changed", sync);
    return () => window.removeEventListener("md:consent-changed", sync);
  }, []);
  const save = (v) => {
    localStorage.setItem(KEY, JSON.stringify({ ...v, ts: new Date().toISOString() }));
    setConsent(v);
    window.dispatchEvent(new Event("md:consent-changed"));
  };
  return { consent, save };
};

export default function CookieBanner() {
  const { consent, save } = useCookieConsent();
  const [open, setOpen] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [prefs, setPrefs] = useState({ essential: true, analytics: false, marketing: false });

  useEffect(() => {
    if (consent) return;
    const t = setTimeout(() => setOpen(true), 800);
    return () => clearTimeout(t);
  }, [consent]);

  // Re-open hook for footer link
  useEffect(() => {
    const onOpen = () => { setAdvanced(true); setOpen(true); setPrefs(consent || { essential: true, analytics: false, marketing: false }); };
    window.addEventListener("md:open-cookie-settings", onOpen);
    return () => window.removeEventListener("md:open-cookie-settings", onOpen);
  }, [consent]);

  if (!open) return null;

  return (
    <div className="md-cookie-banner" role="dialog" aria-label="Cookie preferences" data-testid="cookie-banner">
      <div className="md-cookie-icon"><Cookie size={24} weight="duotone" /></div>
      <div className="md-cookie-body">
        <h3 className="md-cookie-title">{advanced ? "Customise your cookies" : "We use cookies to make MindDhara work."}</h3>
        {!advanced ? (
          <p className="md-cookie-desc">
            Essential cookies keep you signed in and your cart safe. Optional analytics help us improve the experience. You decide.
            {" "}
            <Link to="/cookie-policy" className="md-cookie-link">Cookie Policy</Link>
            {" · "}
            <Link to="/privacy-policy" className="md-cookie-link">Privacy</Link>
          </p>
        ) : (
          <div className="md-cookie-prefs">
            <label className="md-cookie-row" data-testid="cookie-row-essential">
              <input type="checkbox" checked disabled /> <span><strong>Essential</strong>, required for login, cart, sessions. Always on.</span>
            </label>
            <label className="md-cookie-row" data-testid="cookie-row-analytics">
              <input type="checkbox" checked={prefs.analytics} onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })} /> <span><strong>Analytics</strong>, Google Analytics, anonymised page-view tracking.</span>
            </label>
            <label className="md-cookie-row" data-testid="cookie-row-marketing">
              <input type="checkbox" checked={prefs.marketing} onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })} /> <span><strong>Marketing</strong>, currently none used.</span>
            </label>
          </div>
        )}
      </div>
      <div className="md-cookie-actions">
        {!advanced ? (
          <>
            <button onClick={() => setAdvanced(true)} className="md-cookie-secondary" data-testid="cookie-customize">Customise</button>
            <button onClick={() => { save({ essential: true, analytics: false, marketing: false }); setOpen(false); }} className="md-cookie-secondary" data-testid="cookie-reject">Reject Optional</button>
            <button onClick={() => { save({ essential: true, analytics: true, marketing: false }); setOpen(false); }} className="md-cookie-primary" data-testid="cookie-accept-all">Accept All</button>
          </>
        ) : (
          <>
            <button onClick={() => setAdvanced(false)} className="md-cookie-secondary" data-testid="cookie-back">Back</button>
            <button onClick={() => { save(prefs); setOpen(false); setAdvanced(false); }} className="md-cookie-primary" data-testid="cookie-save">Save preferences</button>
          </>
        )}
      </div>
      <button onClick={() => setOpen(false)} className="md-cookie-close" aria-label="Dismiss" data-testid="cookie-dismiss"><X size={14} weight="bold" /></button>
    </div>
  );
}
